/**
 * Face / Beauty / Hair AI report tools - catalog + locked UI config.
 * UI shell: app/editor/_components/ReportToolShell.tsx
 * Each tool is a credit-priced analysis report (not an image editor).
 */

export type ReportTechId = "face-ai" | "beauty-ai" | "hair-ai";
export type ReportToolBadge = "NEW" | "PRO" | "HOT";
export type ReportModeBadge = "PRO" | "HOT";

export const REPORT_AGE_GATE = "16yo or older only. Do not upload photos of minors.";
export const REPORT_ENTERTAINMENT_DISCLAIMER =
  "For entertainment only - not a measure of real beauty, worth, or character.";

export interface ReportModeConfig {
  id: string;
  label: string;
  badge?: ReportModeBadge;
  /** Lucide-ish key rendered by the shell */
  icon: "lightning" | "scan" | "versus" | "plan" | "palette" | "hair" | "shape" | "ratio" | "star" | "mind" | "age" | "vibe" | "heart" | "aura";
  credits: number;
  /** Upload slots (1 selfie, or 2 for PK / compare). */
  slots: number;
  uploadLabel: string;
  headline: string;
  sub: string;
  features: string[];
  resultLabel: string;
  /** Marketing example art - input selfie(s) → result card. */
  exampleImages?: {
    inputs: string[];
    result: string;
  };
}

export interface AiReportToolConfig {
  id: string;
  tech: ReportTechId;
  title: string;
  /** Left-pane subtitle */
  subtitle: string;
  description: string;
  badge?: ReportToolBadge;
  /** Placeholder thumb until we ship real art */
  imageUrl: string;
  /** Analysis dimensions - product contract for the report. */
  dimensions: string[];
  modes: ReportModeConfig[];
  /** Competitors / comps for internal reference (not shown in UI). */
  comps?: string[];
}

export const AI_REPORT_TOOLS: AiReportToolConfig[] = [
  {
    id: "attractiveness-test",
    tech: "face-ai",
    title: "Attractiveness Test",
    subtitle: "Face AI · score, analyze & compare",
    description:
      "Facial beauty report: symmetry, proportions, features, harmony, and overall /10 - plus Deep Scan and 1v1 Face Compare.",
    badge: "HOT",
    imageUrl: "/assets/images/tools/attractiveness-test.webp",
    dimensions: [
      "symmetry",
      "proportions / golden ratio",
      "eyes",
      "nose",
      "lips",
      "jawline",
      "skin",
      "harmony",
      "overall /10",
    ],
    comps: ["Umax", "LooksMax AI", "Overchat Looksmax", "Fotor", "attractivenesstest.com", "faceanalyzer.net", "FaceYourself"],
    modes: [
      // Fast Test - hidden until wired
      // {
      //   id: "fast",
      //   label: "Fast Test",
      //   icon: "lightning",
      //   credits: 5,
      //   slots: 1,
      //   uploadLabel: "Upload your photo",
      //   headline: "Instant Attractiveness Score",
      //   sub: "Upload one selfie and get your overall attractiveness rating in seconds.",
      //   features: [
      //     "Overall 1–10 attractiveness score",
      //     "Your 3 strongest features",
      //     "Percentile vs peers your age",
      //     "Shareable result card",
      //   ],
      //   resultLabel: "Score card",
      // },
      {
        id: "deep",
        label: "Deep Scan",
        icon: "scan",
        credits: 6,
        slots: 1,
        uploadLabel: "Upload your photo",
        headline: "Attractiveness Test",
        sub: "Full facial test breakdown across symmetry, proportions, eyes, nose, lips, jawline, skin, and harmony.",
        features: [
          "All 9 facial beauty dimensions",
          "Symmetry & golden-ratio scoring",
          "Feature-by-feature notes",
          "Shareable full report",
        ],
        resultLabel: "Full report",
        exampleImages: {
          inputs: ["/assets/images/tools/attractiveness-deep-input.webp"],
          result: "/assets/images/tools/attractiveness-deep-result.webp",
        },
      },
      {
        id: "pk",
        label: "Face Compare",
        icon: "versus",
        credits: 12,
        slots: 2,
        uploadLabel: "Upload two photos - Person A vs Person B",
        headline: "1v1 Face Compare",
        sub: "Put two faces head-to-head and see who ranks higher across every key attractiveness metric.",
        features: [
          "Head-to-head scoring",
          "Metric-by-metric winner",
          "Overall PK verdict",
          "Shareable versus card",
        ],
        resultLabel: "Versus card",
        exampleImages: {
          inputs: [
            "/assets/images/tools/attractiveness-pk-input-a.webp",
            "/assets/images/tools/attractiveness-pk-input-b.webp",
          ],
          result: "/assets/images/tools/attractiveness-pk-result.webp",
        },
      },
    ],
  },
  {
    id: "glow-up-plan",
    tech: "face-ai",
    title: "Glow-Up Plan",
    subtitle: "Face AI · looksmaxxing roadmap",
    description:
      "Looksmaxxing glow-up plan: beauty dims plus prioritized fixes, day-by-day roadmap, and AI after preview.",
    badge: "HOT",
    imageUrl: "/assets/images/tools/clear-skin.png",

    dimensions: [
      "attractiveness dims",
      "prioritized fixes",
      "day-by-day roadmap",
      "AI after preview",
    ],
    comps: ["Umax", "Overchat", "LooksMaxxing (App Store)", "Media.io Glow-Up"],
    modes: [
      {
        id: "quick",
        label: "Quick Plan",
        icon: "plan",
        credits: 8,
        slots: 1,
        uploadLabel: "Upload your photo",
        headline: "Your Glow-Up Priorities",
        sub: "See the highest-impact fixes first - ranked by expected visual payoff.",
        features: [
          "Top 5 prioritized fixes",
          "Impact vs effort ranking",
          "Quick-win checklist",
          "Shareable plan card",
        ],
        resultLabel: "Priority plan",
      },
      {
        id: "full",
        label: "Full Roadmap",
        badge: "PRO",
        icon: "scan",
        credits: 15,
        slots: 1,
        uploadLabel: "Upload your photo",
        headline: "Looksmaxxing Glow-Up Roadmap",
        sub: "Day-by-day plan plus an AI preview of your potential after.",
        features: [
          "Full facial analysis baseline",
          "Day-by-day glow-up roadmap",
          "AI after preview",
          "Weekly milestones",
        ],
        resultLabel: "Roadmap + after",
      },
    ],
  },
  {
    id: "skin-analysis",
    tech: "beauty-ai",
    title: "Skin Analysis",
    subtitle: "Beauty AI · skincare report",
    description:
      "Skincare report: fine lines, texture, volume, eye-area aging, tone/pigmentation, estimated age, and routine.",
    badge: "NEW",
    imageUrl: "/assets/images/tools/clear-skin.png",

    dimensions: [
      "fine lines",
      "texture / elasticity",
      "volume / sagging",
      "eye-area aging",
      "tone / pigmentation",
      "estimated age",
      "routine",
    ],
    comps: ["Perfect Corp AI Face Analyzer", "skincare brand tools"],
    modes: [
      {
        id: "quick",
        label: "Quick Scan",
        icon: "lightning",
        credits: 6,
        slots: 1,
        uploadLabel: "Upload a clear face photo",
        headline: "Instant Skin Snapshot",
        sub: "Fast read on texture, tone, and estimated skin age from one photo.",
        features: [
          "Skin age estimate",
          "Tone & texture flags",
          "Top concern callout",
          "Starter routine tips",
        ],
        resultLabel: "Skin snapshot",
      },
      {
        id: "full",
        label: "Full Report",
        badge: "PRO",
        icon: "scan",
        credits: 12,
        slots: 1,
        uploadLabel: "Upload a clear face photo",
        headline: "Complete Skincare Report",
        sub: "Lines, elasticity, volume, eye-area aging, pigmentation - plus a tailored routine.",
        features: [
          "All 7 skin dimensions",
          "Eye-area aging score",
          "Pigmentation map notes",
          "Personalized routine",
        ],
        resultLabel: "Skincare report",
      },
    ],
  },
  {
    id: "color-analysis",
    tech: "beauty-ai",
    title: "Color Analysis",
    subtitle: "Beauty AI · seasonal palette",
    description:
      "Seasonal color analysis: season, best colors, neutrals, colors to avoid, jewelry tones, hair ideas, and swatches.",
    badge: "NEW",
    imageUrl: "/assets/images/tools/color-analysis.webp",

    dimensions: [
      "season (spring / summer / autumn / winter)",
      "best colors",
      "neutrals",
      "colors to avoid",
      "jewelry tones",
      "hair ideas",
      "swatches",
    ],
    comps: ["Lorka", "Media.io"],
    modes: [
      {
        id: "fast",
        label: "Fast Season",
        icon: "palette",
        credits: 5,
        slots: 1,
        uploadLabel: "Upload a natural-light selfie",
        headline: "What Season Am I?",
        sub: "Find your seasonal color type and a starter palette of best-fit shades.",
        features: [
          "Season verdict",
          "Best color swatches",
          "Neutrals that flatter",
          "Colors to avoid",
        ],
        resultLabel: "Season card",
      },
      {
        id: "full",
        label: "Full Palette",
        badge: "PRO",
        icon: "scan",
        credits: 10,
        slots: 1,
        uploadLabel: "Upload a natural-light selfie",
        headline: "Complete Seasonal Palette",
        sub: "Full swatch book plus jewelry tones and hair color ideas for your season.",
        features: [
          "Expanded swatch book",
          "Jewelry metal tones",
          "Hair color ideas",
          "Outfit pairing tips",
        ],
        resultLabel: "Palette report",
      },
    ],
  },
  {
    id: "hair-analysis",
    tech: "hair-ai",
    title: "Hair Analysis",
    subtitle: "Hair AI · type & try-on chart",
    description:
      "Hair analysis and hairstyle try-on: hair type, face-shape-matched cuts, and side-by-side style chart.",
    badge: "NEW",
    imageUrl: "/assets/images/tools/change-hair-color.png",

    dimensions: [
      "hair type",
      "face-shape-matched cuts",
      "side-by-side hairstyles",
    ],
    modes: [
      {
        id: "analyze",
        label: "Analyze",
        icon: "hair",
        credits: 6,
        slots: 1,
        uploadLabel: "Upload a clear headshot",
        headline: "Hair Type & Face Shape Match",
        sub: "Detect your hair type and the cuts that flatter your face shape.",
        features: [
          "Hair type classification",
          "Face-shape match",
          "Recommended cuts",
          "Styling notes",
        ],
        resultLabel: "Hair report",
      },
      {
        id: "tryon",
        label: "Try-On Chart",
        badge: "HOT",
        icon: "scan",
        credits: 12,
        slots: 1,
        uploadLabel: "Upload a clear headshot",
        headline: "Hairstyle Try-On Chart",
        sub: "Side-by-side hairstyle options matched to your face - pick your next look.",
        features: [
          "Side-by-side style chart",
          "Face-shape-matched picks",
          "Length & texture variants",
          "Shareable try-on card",
        ],
        resultLabel: "Style chart",
      },
    ],
  },
  {
    id: "face-shape",
    tech: "face-ai",
    title: "Face Shape Guide",
    subtitle: "Face AI · shape + styling",
    description:
      "Face-shape guide: oval, round, square, heart, or diamond - plus styling implications.",
    badge: "NEW",
    imageUrl: "/assets/images/tools/face-shape.webp",

    dimensions: ["oval / round / square / heart / diamond", "styling implications"],
    comps: ["faceshapetool.com"],
    modes: [
      {
        id: "guide",
        label: "Shape Guide",
        icon: "shape",
        credits: 4,
        slots: 1,
        uploadLabel: "Upload a front-facing photo",
        headline: "Your Face Shape",
        sub: "Identify your face shape and get styling implications for hair, glasses, and more.",
        features: [
          "Primary face shape",
          "Confidence notes",
          "Hair styling tips",
          "Glasses & accessory tips",
        ],
        resultLabel: "Shape guide",
      },
    ],
  },
  {
    id: "golden-ratio",
    tech: "face-ai",
    title: "Golden Ratio Report",
    subtitle: "Face AI · proportions & phi",
    description:
      "Golden-ratio / proportion report: phi ratios, thirds/fifths, and harmony score.",
    badge: "NEW",
    imageUrl: "/assets/images/tools/hd-detailer.png",

    dimensions: ["phi ratios", "thirds / fifths", "harmony score"],
    comps: ["calqulate.net", "Beauty Scanner apps"],
    modes: [
      {
        id: "fast",
        label: "Fast Ratio",
        icon: "ratio",
        credits: 5,
        slots: 1,
        uploadLabel: "Upload a front-facing photo",
        headline: "Proportion Snapshot",
        sub: "Quick phi and facial-thirds check with an overall harmony score.",
        features: [
          "Harmony score",
          "Key phi ratios",
          "Thirds overview",
          "Shareable score card",
        ],
        resultLabel: "Ratio card",
      },
      {
        id: "full",
        label: "Full Report",
        badge: "PRO",
        icon: "scan",
        credits: 10,
        slots: 1,
        uploadLabel: "Upload a front-facing photo",
        headline: "Golden Ratio Report",
        sub: "Detailed thirds/fifths mapping and phi ratios across facial landmarks.",
        features: [
          "Full thirds & fifths map",
          "Phi ratio breakdown",
          "Landmark notes",
          "Harmony deep-dive",
        ],
        resultLabel: "Proportion report",
      },
    ],
  },
  {
    id: "celebrity-lookalike",
    tech: "face-ai",
    title: "Celebrity Look-Alike",
    subtitle: "Face AI · famous face matches",
    description: "Celebrity look-alike: top matches with similarity percentages.",
    badge: "HOT",
    imageUrl: "/assets/images/tools/ai_photoshoot_promo_4.png",

    dimensions: ["top-3 / top-4 matches", "similarity %"],
    comps: ["Overchat Celebrity Look-Alike", "Find My Celebrity Look Alike GPT"],
    modes: [
      {
        id: "fast",
        label: "Top Matches",
        icon: "star",
        credits: 5,
        slots: 1,
        uploadLabel: "Upload your photo",
        headline: "Your Celebrity Matches",
        sub: "See your top look-alikes with similarity scores - fun, shareable, viral.",
        features: [
          "Top 3 celebrity matches",
          "Similarity % each",
          "Match reasons",
          "Shareable result card",
        ],
        resultLabel: "Match card",
      },
      {
        id: "deep",
        label: "Deep Match",
        badge: "PRO",
        icon: "scan",
        credits: 8,
        slots: 1,
        uploadLabel: "Upload your photo",
        headline: "Deep Celebrity Look-Alike",
        sub: "Expanded match list with feature-level similarity notes.",
        features: [
          "Top 4+ matches",
          "Feature-level similarity",
          "Gender & era filters",
          "Share pack",
        ],
        resultLabel: "Match report",
      },
    ],
  },
  {
    id: "face-reading",
    tech: "face-ai",
    title: "Face Reading",
    subtitle: "Face AI · physiognomy traits",
    description:
      "Personality-from-face / physiognomy: traits inferred from facial features. Entertainment only.",
    badge: "NEW",
    imageUrl: "/assets/images/tools/ai-designer.png",

    dimensions: ["traits inferred from features"],
    comps: ["Beauty Scanner apps"],
    modes: [
      {
        id: "fast",
        label: "Quick Read",
        icon: "mind",
        credits: 5,
        slots: 1,
        uploadLabel: "Upload your photo",
        headline: "Face Reading Snapshot",
        sub: "A light physiognomy-inspired trait read from your facial features.",
        features: [
          "Core trait summary",
          "Feature → trait notes",
          "Personality vibe tags",
          "Shareable reading card",
        ],
        resultLabel: "Reading card",
      },
      {
        id: "full",
        label: "Full Reading",
        badge: "PRO",
        icon: "scan",
        credits: 8,
        slots: 1,
        uploadLabel: "Upload your photo",
        headline: "Complete Face Reading",
        sub: "Deeper trait map across brows, eyes, nose, lips, jaw - framed as entertainment.",
        features: [
          "Full trait map",
          "Feature-by-feature notes",
          "Strength & soft-spot traits",
          "Long-form reading",
        ],
        resultLabel: "Full reading",
      },
    ],
  },
  {
    id: "age-estimate",
    tech: "beauty-ai",
    title: "Age Estimate",
    subtitle: "Beauty AI · estimated vs actual",
    description: "Age estimate: AI-estimated age from your photo (often paired with skin reports).",
    badge: "NEW",
    imageUrl: "/assets/images/tools/age-filter.png",

    dimensions: ["estimated age", "vs stated age (optional)"],
    comps: ["Media.io AI Age Filter"],
    modes: [
      {
        id: "estimate",
        label: "Estimate",
        icon: "age",
        credits: 3,
        slots: 1,
        uploadLabel: "Upload your photo",
        headline: "How Old Do You Look?",
        sub: "Get an AI age estimate from your selfie - fun, not clinical.",
        features: [
          "Estimated age",
          "Age range band",
          "Youthful / mature cues",
          "Shareable result",
        ],
        resultLabel: "Age card",
      },
    ],
  },
  {
    id: "vibe-analysis",
    tech: "face-ai",
    title: "Vibe Analysis",
    subtitle: "Face AI · aesthetic archetype",
    description:
      "Aesthetic / vibe analysis: archetype (old money, clean girl, streetwear…) plus a moodboard.",
    badge: "NEW",
    imageUrl: "/assets/images/tools/anime-styling.png",

    dimensions: ["aesthetic archetype", "moodboard"],
    modes: [
      {
        id: "fast",
        label: "Fast Vibe",
        icon: "vibe",
        credits: 5,
        slots: 1,
        uploadLabel: "Upload your photo",
        headline: "Your Aesthetic Archetype",
        sub: "Old money, clean girl, streetwear, and more - find the vibe you project.",
        features: [
          "Primary archetype",
          "Secondary vibe tags",
          "Style keywords",
          "Shareable vibe card",
        ],
        resultLabel: "Vibe card",
      },
      {
        id: "moodboard",
        label: "Moodboard",
        badge: "PRO",
        icon: "palette",
        credits: 10,
        slots: 1,
        uploadLabel: "Upload your photo",
        headline: "Aesthetic Moodboard",
        sub: "Full archetype breakdown plus a moodboard of looks, colors, and references.",
        features: [
          "Expanded archetype map",
          "Visual moodboard",
          "Color & fit cues",
          "Reference looks",
        ],
        resultLabel: "Moodboard",
      },
    ],
  },
  {
    id: "dating-profile-score",
    tech: "face-ai",
    title: "Dating Photo Score",
    subtitle: "Face AI · rate my profile photo",
    description:
      "Dating-profile / rate-my-photo: profile strength score plus concrete suggestions - a lower-risk reframing of attractiveness.",
    badge: "NEW",
    imageUrl: "/assets/images/tools/ai_photoshoot_promo_7.png",

    dimensions: ["profile strength", "suggestions"],
    modes: [
      {
        id: "rate",
        label: "Rate Photo",
        icon: "heart",
        credits: 5,
        slots: 1,
        uploadLabel: "Upload a dating profile photo",
        headline: "How Strong Is This Photo?",
        sub: "Score this image as a dating profile photo - lighting, expression, framing, and first impression.",
        features: [
          "Profile strength score",
          "First-impression notes",
          "Lighting & framing tips",
          "Quick fix suggestions",
        ],
        resultLabel: "Score card",
      },
      {
        id: "coach",
        label: "Photo Coach",
        badge: "PRO",
        icon: "scan",
        credits: 10,
        slots: 1,
        uploadLabel: "Upload a dating profile photo",
        headline: "Dating Photo Coach",
        sub: "Deeper feedback with prioritized suggestions to make this photo pull harder.",
        features: [
          "Detailed strength breakdown",
          "Prioritized suggestions",
          "Do / don't checklist",
          "Reshoot brief",
        ],
        resultLabel: "Coach report",
      },
    ],
  },
  {
    id: "aura-reading",
    tech: "face-ai",
    title: "Aura Reading",
    subtitle: "Face AI · energy colors",
    description:
      "Aura / energy reading: aura colors and meanings - spiritual, not clinical. Big TikTok presence.",
    badge: "NEW",
    imageUrl: "/assets/images/tools/photo-filters.png",

    dimensions: ["aura colors", "meaning"],
    modes: [
      {
        id: "read",
        label: "Aura Read",
        icon: "aura",
        credits: 4,
        slots: 1,
        uploadLabel: "Upload your photo",
        headline: "Your Aura Colors",
        sub: "A spiritual, entertainment-first aura read - colors, energy, and meaning.",
        features: [
          "Primary aura color",
          "Secondary hues",
          "Energy meaning",
          "Shareable aura card",
        ],
        resultLabel: "Aura card",
      },
    ],
  },
];

/** Only these are live in dashboard / editor. Rest are catalogued as coming soon. */
export const LIVE_AI_REPORT_TOOL_IDS = new Set(["attractiveness-test"]);

/** Dashboard / sidebar badge - first mode credits. Charging always uses modes[].credits. */
export function getAiReportDisplayCreditCost(tool: AiReportToolConfig): number {
  const first = tool.modes[0]?.credits;
  if (typeof first !== "number") {
    throw new Error(`[ai-report-tools] Tool "${tool.id}" has no mode credits`);
  }
  return first;
}

export const getAiReportTool = (id: string): AiReportToolConfig | undefined =>
  AI_REPORT_TOOLS.find((t) => t.id === id);

export const AI_REPORT_TOOL_IDS = AI_REPORT_TOOLS.map((t) => t.id);

export const isAiReportToolId = (id: string): boolean =>
  AI_REPORT_TOOL_IDS.includes(id);
